import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MAX_PAGES = 10; // Cap for document previews (e.g. 1–2 for Aadhaar)

/**
 * Renders all pages of a PDF from base64 data using react-pdf.
 * Used on Android/mobile where iframe + blob URL often shows blank.
 * react-pdf provides reliable PDF rendering across mobile browsers.
 */
const PdfCanvasPreview = ({ file, openPdfLabel = "Open PDF", previewHint, onError }) => {
	const [numPages, setNumPages] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	function onDocumentLoadSuccess({ numPages: totalPages }) {
		const total = Math.min(totalPages, MAX_PAGES);
		setNumPages(total);
		setLoading(false);
		setError(null);
	}

	function onDocumentLoadError(err) {
		console.error("[PdfCanvasPreview] Load failed:", err);
		setError(err?.message || "Failed to load PDF");
		setLoading(false);
		onError?.(err);
	}

	if (!file) return null;

	return (
		<div className="flex flex-col w-full items-center" style={{ gap: 8, height: "100%", minHeight: "400px", width: "100%", margin: 0, padding: 0 }}>
			<div
				className="w-full flex flex-col items-start pdf-preview-container"
				style={{
					height: "100%",
					minHeight: "400px",
					maxHeight: "70vh",
					overflowY: "auto",
					overflowX: "auto",
					background: "#f5f5f5",
					borderRadius: 4,
					gap: 8,
					padding: "8px 8px 8px 8px",
					WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
					width: "100%",
					position: "relative",
					margin: 0,
				}}
			>
				{loading && (
					<div
						className="flex items-center justify-center w-full text-[#656565] text-sm"
						style={{ minHeight: 200 }}
					>
						Loading PDF…
					</div>
				)}
				{error && (
					<div
						className="flex items-center justify-center w-full text-[#656565] text-sm text-center px-4"
						style={{ minHeight: 200 }}
					>
						{error || "Unable to show preview."}
					</div>
				)}
				<Document
					file={file}
					onLoadSuccess={onDocumentLoadSuccess}
					onLoadError={onDocumentLoadError}
					loading={null}
					error={null}
					className="flex flex-col items-center"
					style={{ gap: 8, minWidth: "fit-content", margin: 0 }}
				>
					{numPages &&
						Array.from({ length: numPages }, (_, i) => (
							<Page
								key={`page_${i + 1}`}
								pageNumber={i + 1}
								renderTextLayer={false}
								renderAnnotationLayer={false}
								width={Math.min(window.innerWidth - 40, 600)}
								className="shadow-sm"
								style={{
									minWidth: "fit-content",
									marginBottom: 8,
									marginLeft: 0,
									marginRight: 0,
								}}
							/>
						))}
				</Document>
			</div>
			{/* Only show Open PDF and hint when preview is not available (loading or error) */}
			{(loading || error) && (
				<>
					{typeof file === "string" && file.startsWith("data:") && (
						<a
							href={file}
							target="_blank"
							rel="noopener noreferrer"
							className="text-sm font-medium"
							style={{
								color: "#1658aa",
								textDecoration: "none",
								padding: "8px 16px",
								border: "1px solid #1658aa",
								borderRadius: 4,
							}}
						>
							{openPdfLabel}
						</a>
					)}
					{previewHint && (
						<p className="text-[#656565] text-xs text-center" style={{ margin: 0 }}>
							{previewHint}
						</p>
					)}
				</>
			)}
		</div>
	);
};

export default PdfCanvasPreview;
